/**
 * FineIndex Database Layer
 * 
 * Optimized Dexie wrapper for asset index storage.
 * Uses bulk operations and aggressive caching to minimize
 * IndexedDB overhead.
 * 
 * @module @engine/aid/FineIndexDB
 */

import Dexie, { type Table } from 'dexie';
import type { 
  AssetEntry, 
  AssetQuery, 
  AssetQueryResult,
  IndexStats,
  ProgressCallback,
  IFineIndexDB,
} from './types';

// ============================================
// DATABASE SCHEMA
// ============================================

interface AssetRecord {
  guid: string;
  path: string;
  contentType: string;
  importerType: string;
  importerParams: string; // JSON stringified
  size: number;
  contentHash: string;
  createdAt: number; // timestamp
  updatedAt: number; // timestamp
  fileModifiedAt: number; // timestamp
  labels: string; // JSON stringified array
  importStatus: 'pending' | 'imported' | 'failed';
  importError?: string;
  artifacts?: string; // JSON stringified
  dependencies?: string; // JSON stringified
  importedData?: string; // JSON stringified
  producedAssets?: string; // JSON stringified ProducedAsset[] (legacy)
  sourceGuid?: string; // GUID of source asset if this is a produced/derived asset
}

interface MetaRecord {
  key: string;
  value: string;
}

class FineIndexDatabase extends Dexie {
  assets!: Table<AssetRecord, string>;
  meta!: Table<MetaRecord, string>;

  constructor(dbName: string) {
    super(dbName);
    
    // Optimized schema with compound indexes
    this.version(3).stores({
      // Primary key: guid
      // Indexes: path (unique), contentType, importerType, updatedAt, sourceGuid
      assets: 'guid, &path, contentType, importerType, updatedAt, sourceGuid, [contentType+updatedAt], [importerType+updatedAt]',
      meta: 'key',
    });
  }
}

// ============================================
// RECORD CONVERSION
// ============================================

function entryToRecord(entry: AssetEntry): AssetRecord {
  // Serialize producedAssets with timestamps instead of Date objects
  const producedAssetsSerialized = entry.producedAssets?.map(p => ({
    ...p,
    producedAt: p.producedAt instanceof Date ? p.producedAt.getTime() : p.producedAt,
  }));
  
  return {
    guid: entry.guid,
    path: entry.path,
    contentType: entry.contentType,
    importerType: entry.importerType,
    importerParams: JSON.stringify(entry.importerParams),
    size: entry.size,
    contentHash: entry.contentHash,
    createdAt: entry.createdAt.getTime(),
    updatedAt: entry.updatedAt.getTime(),
    fileModifiedAt: entry.fileModifiedAt.getTime(),
    labels: JSON.stringify(entry.labels),
    importStatus: entry.importStatus,
    importError: entry.importError,
    artifacts: entry.artifacts ? JSON.stringify(entry.artifacts) : undefined,
    dependencies: entry.dependencies ? JSON.stringify(entry.dependencies) : undefined,
    importedData: entry.importedData ? JSON.stringify(entry.importedData) : undefined,
    producedAssets: producedAssetsSerialized ? JSON.stringify(producedAssetsSerialized) : undefined,
    sourceGuid: entry.sourceGuid,
  };
}

function recordToEntry(record: AssetRecord): AssetEntry {
  // Deserialize producedAssets with Date objects
  const producedAssetsRaw = record.producedAssets ? JSON.parse(record.producedAssets) : undefined;
  const producedAssets = producedAssetsRaw?.map((p: { producedAt: number | string }) => ({
    ...p,
    producedAt: new Date(p.producedAt),
  }));
  
  return {
    guid: record.guid,
    path: record.path,
    contentType: record.contentType,
    importerType: record.importerType,
    importerParams: JSON.parse(record.importerParams),
    size: record.size,
    contentHash: record.contentHash,
    createdAt: new Date(record.createdAt),
    updatedAt: new Date(record.updatedAt),
    fileModifiedAt: new Date(record.fileModifiedAt),
    labels: JSON.parse(record.labels),
    importStatus: record.importStatus,
    importError: record.importError,
    artifacts: record.artifacts ? JSON.parse(record.artifacts) : undefined,
    dependencies: record.dependencies ? JSON.parse(record.dependencies) : undefined,
    importedData: record.importedData ? JSON.parse(record.importedData) : undefined,
    producedAssets,
    sourceGuid: record.sourceGuid,
  };
}

// ============================================
// DATABASE WRAPPER
// ============================================

export class FineIndexDB implements IFineIndexDB {
  private db: FineIndexDatabase | null = null;
  private dbName: string;
  
  // In-memory caches for fast access
  private guidToPath = new Map<string, string>();
  private pathToGuid = new Map<string, string>();
  private cacheValid = false;

  constructor(dbName: string = 'FineIndex') {
    this.dbName = dbName;
  }

  // ============================================
  // LIFECYCLE
  // ============================================

  async init(): Promise<void> {
    if (this.db) return;
    
    this.db = new FineIndexDatabase(this.dbName);
    await this.db.open();
    
    // Build initial cache
    await this.rebuildCache();
  }

  async close(): Promise<void> {
    if (!this.db) return;
    
    this.db.close();
    this.db = null;
    this.clearCache();
  }

  isReady(): boolean {
    return this.db !== null && this.db.isOpen();
  }

  private ensureReady(): void {
    if (!this.isReady()) {
      throw new Error('FineIndexDB not initialized. Call init() first.');
    }
  }

  // ============================================
  // CACHE MANAGEMENT
  // ============================================

  private clearCache(): void {
    this.guidToPath.clear();
    this.pathToGuid.clear();
    this.cacheValid = false;
  }

  private async rebuildCache(): Promise<void> {
    this.ensureReady();
    
    this.clearCache();
    
    // Single query to get all guid/path pairs - only select needed fields
    const records = await this.db!.assets.toCollection().primaryKeys();
    const paths = await this.db!.assets.orderBy('path').keys();
    
    // Get guid->path mapping efficiently
    const allRecords = await this.db!.assets.toArray();
    
    for (const record of allRecords) {
      this.guidToPath.set(record.guid, record.path);
      this.pathToGuid.set(record.path, record.guid);
    }
    
    this.cacheValid = true;
  }

  private updateCache(entry: AssetEntry): void {
    // Remove old path mapping if it exists
    const oldPath = this.guidToPath.get(entry.guid);
    if (oldPath && oldPath !== entry.path) {
      this.pathToGuid.delete(oldPath);
    }
    
    // Remove old guid mapping if path changed
    const oldGuid = this.pathToGuid.get(entry.path);
    if (oldGuid && oldGuid !== entry.guid) {
      this.guidToPath.delete(oldGuid);
    }
    
    this.guidToPath.set(entry.guid, entry.path);
    this.pathToGuid.set(entry.path, entry.guid);
  }

  private removeFromCache(guid: string): void {
    const path = this.guidToPath.get(guid);
    if (path) {
      this.pathToGuid.delete(path);
    }
    this.guidToPath.delete(guid);
  }

  // ============================================
  // FAST LOOKUPS (from cache)
  // ============================================

  getGuidForPath(path: string): string | null {
    return this.pathToGuid.get(path) ?? null;
  }

  getPathForGuid(guid: string): string | null {
    return this.guidToPath.get(guid) ?? null;
  }

  hasPath(path: string): boolean {
    return this.pathToGuid.has(path);
  }

  hasGuid(guid: string): boolean {
    return this.guidToPath.has(guid);
  }

  getAllPaths(): string[] {
    return Array.from(this.pathToGuid.keys());
  }

  getAllGuids(): string[] {
    return Array.from(this.guidToPath.keys());
  }

  // ============================================
  // SINGLE OPERATIONS
  // ============================================

  async getByGuid(guid: string): Promise<AssetEntry | null> {
    this.ensureReady();
    
    const record = await this.db!.assets.get(guid);
    return record ? recordToEntry(record) : null;
  }

  async getByPath(path: string): Promise<AssetEntry | null> {
    this.ensureReady();
    
    const record = await this.db!.assets.where('path').equals(path).first();
    return record ? recordToEntry(record) : null;
  }

  async getBySourceGuid(sourceGuid: string): Promise<AssetEntry[]> {
    this.ensureReady();
    
    const records = await this.db!.assets.where('sourceGuid').equals(sourceGuid).toArray();
    return records.map(recordToEntry);
  }

  async put(entry: AssetEntry): Promise<void> {
    this.ensureReady();
    
    await this.db!.assets.put(entryToRecord(entry));
    this.updateCache(entry);
  }

  async deleteByGuid(guid: string): Promise<boolean> {
    this.ensureReady();
    
    const existed = this.hasGuid(guid);
    if (existed) {
      await this.db!.assets.delete(guid);
      this.removeFromCache(guid);
    }
    return existed;
  }

  async deleteByPath(path: string): Promise<boolean> {
    this.ensureReady();
    
    const guid = this.getGuidForPath(path);
    if (guid) {
      return this.deleteByGuid(guid);
    }
    return false;
  }

  /**
   * Delete all produced entries (entries that have a sourceGuid set).
   * Used during full reindex to clear stale produced assets.
   */
  async deleteAllProducedEntries(): Promise<number> {
    this.ensureReady();
    
    // Get all entries with sourceGuid set
    const records = await this.db!.assets.where('sourceGuid').above('').toArray();
    const guids = records.map(r => r.guid);
    
    // Delete them
    for (const guid of guids) {
      await this.db!.assets.delete(guid);
      this.removeFromCache(guid);
    }
    
    return guids.length;
  }

  // ============================================
  // BULK OPERATIONS (optimized for Dexie)
  // ============================================

  async bulkPut(entries: AssetEntry[], onProgress?: ProgressCallback): Promise<void> {
    this.ensureReady();
    
    if (entries.length === 0) return;
    
    const records = entries.map(entryToRecord);
    const BATCH_SIZE = 500;
    
    for (let i = 0; i < records.length; i += BATCH_SIZE) {
      const batch = records.slice(i, i + BATCH_SIZE);
      await this.db!.assets.bulkPut(batch);
      
      // Update cache for this batch
      for (let j = 0; j < batch.length; j++) {
        const entry = entries[i + j];
        this.updateCache(entry);
      }
      
      if (onProgress) {
        onProgress({
          current: Math.min(i + BATCH_SIZE, records.length),
          total: records.length,
          phase: 'updating',
        });
      }
    }
  }

  async bulkDelete(guids: string[], onProgress?: ProgressCallback): Promise<void> {
    this.ensureReady();
    
    if (guids.length === 0) return;
    
    const BATCH_SIZE = 500;
    
    for (let i = 0; i < guids.length; i += BATCH_SIZE) {
      const batch = guids.slice(i, i + BATCH_SIZE);
      await this.db!.assets.bulkDelete(batch);
      
      // Update cache
      for (const guid of batch) {
        this.removeFromCache(guid);
      }
      
      if (onProgress) {
        onProgress({
          current: Math.min(i + BATCH_SIZE, guids.length),
          total: guids.length,
          phase: 'updating',
        });
      }
    }
  }

  // ============================================
  // QUERIES (optimized with indexes)
  // ============================================

  async getByContentType(contentType: string): Promise<AssetEntry[]> {
    this.ensureReady();
    
    const records = await this.db!.assets
      .where('contentType')
      .equals(contentType)
      .toArray();
    
    return records.map(recordToEntry);
  }

  async getByImporterType(importerType: string): Promise<AssetEntry[]> {
    this.ensureReady();
    
    const records = await this.db!.assets
      .where('importerType')
      .equals(importerType)
      .toArray();
    
    return records.map(recordToEntry);
  }

  async query(query: AssetQuery): Promise<AssetQueryResult> {
    this.ensureReady();
    
    let collection = this.db!.assets.toCollection();
    
    // Apply filters
    if (query.contentType) {
      const types = Array.isArray(query.contentType) ? query.contentType : [query.contentType];
      collection = this.db!.assets.where('contentType').anyOf(types);
    } else if (query.importerType) {
      const types = Array.isArray(query.importerType) ? query.importerType : [query.importerType];
      collection = this.db!.assets.where('importerType').anyOf(types);
    }
    
    // Get all matching records
    let records = await collection.toArray();
    
    // Apply path prefix filter (in memory - compound index would be complex)
    if (query.pathPrefix) {
      const prefix = query.pathPrefix.endsWith('/') ? query.pathPrefix : query.pathPrefix + '/';
      records = records.filter(r => r.path.startsWith(prefix) || r.path === query.pathPrefix);
    }
    
    // Apply labels filter (in memory)
    if (query.labels && query.labels.length > 0) {
      const labelSet = new Set(query.labels);
      records = records.filter(r => {
        const labels: string[] = JSON.parse(r.labels);
        return labels.some(l => labelSet.has(l));
      });
    }
    
    const totalCount = records.length;
    
    // Sort
    if (query.sortBy) {
      const dir = query.sortOrder === 'desc' ? -1 : 1;
      records.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        
        switch (query.sortBy) {
          case 'path':
            aVal = a.path;
            bVal = b.path;
            break;
          case 'contentType':
            aVal = a.contentType;
            bVal = b.contentType;
            break;
          case 'updatedAt':
            aVal = a.updatedAt;
            bVal = b.updatedAt;
            break;
          case 'size':
            aVal = a.size;
            bVal = b.size;
            break;
          default:
            return 0;
        }
        
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }
    
    // Pagination
    if (query.offset) {
      records = records.slice(query.offset);
    }
    if (query.limit) {
      records = records.slice(0, query.limit);
    }
    
    return {
      entries: records.map(recordToEntry),
      totalCount,
    };
  }

  // ============================================
  // STATISTICS
  // ============================================

  async count(): Promise<number> {
    this.ensureReady();
    return this.db!.assets.count();
  }

  async getStats(): Promise<IndexStats> {
    this.ensureReady();
    
    const records = await this.db!.assets.toArray();
    
    const byContentType: Record<string, number> = {};
    const byImporterType: Record<string, number> = {};
    let totalSize = 0;
    
    for (const record of records) {
      byContentType[record.contentType] = (byContentType[record.contentType] || 0) + 1;
      byImporterType[record.importerType] = (byImporterType[record.importerType] || 0) + 1;
      totalSize += record.size;
    }
    
    const lastRefreshStr = await this.getMeta('lastRefresh');
    const lastRefresh = lastRefreshStr ? new Date(parseInt(lastRefreshStr, 10)) : null;
    
    return {
      totalAssets: records.length,
      byContentType,
      byImporterType,
      totalSize,
      lastRefresh,
    };
  }

  // ============================================
  // META
  // ============================================

  async getMeta(key: string): Promise<string | null> {
    this.ensureReady();
    const record = await this.db!.meta.get(key);
    return record?.value ?? null;
  }

  async setMeta(key: string, value: string): Promise<void> {
    this.ensureReady();
    await this.db!.meta.put({ key, value });
  }

  async setLastRefresh(date: Date): Promise<void> {
    await this.setMeta('lastRefresh', date.getTime().toString());
  }

  // ============================================
  // CLEAR
  // ============================================

  async clear(): Promise<void> {
    this.ensureReady();
    await this.db!.assets.clear();
    await this.db!.meta.clear();
    this.clearCache();
  }
}

// ============================================
// DELETE DATABASE
// ============================================

export async function deleteFineIndexDB(dbName: string): Promise<void> {
  await Dexie.delete(dbName);
}
